/**
 * Domain Names — master registrar page (admin).
 * Multi-account TPP Wholesale (REST + legacy) with per-account sync,
 * a "Show expired" toggle, per-domain renewal cost from the API and
 * account badges so it's clear which reseller each domain belongs to.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useClients } from "@/contexts/ClientContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Globe2, KeyRound, Loader2, Pencil, Plus, RefreshCw,
  Search, Star, Trash2, X, Save,
} from "lucide-react";
import { DomainDetailPanel } from "@/components/domains/DomainDetailPanel";
import { expiryBadge } from "@/components/domains/ZoneRecordsEditor";

const emptyAccountForm = {
  account_id: null,
  label: "",
  auth_type: "rest",
  api_key: "",
  secret_key: "",
  account_no: "",
  login: "",
  password: "",
};

export default function DomainsPage() {
  const { clients } = useClients();
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accForm, setAccForm] = useState(emptyAccountForm);
  const [showAccForm, setShowAccForm] = useState(false);
  const [accBusy, setAccBusy] = useState(false);
  const [serverIp, setServerIp] = useState(null);

  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [showExpired, setShowExpired] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  // Pending client-assignment edits — keyed by domain_id. Applied in bulk
  // by the top-right "UPDATE" button so admins can queue up many changes
  // without the list refreshing after each dropdown selection.
  //   value === undefined  → no change (use the domain's current client)
  //   value === "_none"    → unassign
  //   value === "<cid>"    → assign to that client
  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(false);

  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const { data } = await api.get("/domains/tpp/accounts");
      setAccounts(data?.accounts || []);
    } catch { setAccounts([]); }
    finally { setAccountsLoading(false); }
  }, []);

  const loadDomains = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/domains", {
        params: {
          search, assigned: assignedFilter, show_expired: showExpired,
          account_id: accountFilter === "all" ? "" : accountFilter,
        },
      });
      setDomains(data?.domains || []);
    } catch { setDomains([]); }
    finally { setLoading(false); }
  }, [search, assignedFilter, showExpired, accountFilter]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => {
    const t = setTimeout(loadDomains, 250);
    return () => clearTimeout(t);
  }, [loadDomains]);
  useEffect(() => {
    api.get("/domains/server-ip").then((r) => setServerIp(r.data?.ip)).catch(() => {});
  }, []);

  const beginAddAccount = () => {
    setAccForm(emptyAccountForm);
    setShowAccForm(true);
  };

  const beginEditAccount = (a) => {
    setAccForm({
      account_id: a.account_id, label: a.label || "", auth_type: a.auth_type,
      api_key: a.api_key_last4 ? `••••${a.api_key_last4}` : "",
      secret_key: "", account_no: a.account_no || "", login: a.login || "", password: "",
    });
    setShowAccForm(true);
  };

  const saveAccount = async () => {
    if (!accForm.label.trim()) { toast.error("A label is required"); return; }
    setAccBusy(true);
    try {
      const payload = { label: accForm.label.trim() };
      if (accForm.auth_type === "rest") {
        // Only send api_key if user actually typed something new (not the ••••1234 mask).
        if (accForm.api_key && !accForm.api_key.startsWith("••••")) {
          payload.api_key = accForm.api_key.trim();
        }
        if (accForm.secret_key) payload.secret_key = accForm.secret_key.trim();
      } else {
        payload.account_no = accForm.account_no.trim();
        payload.login = accForm.login.trim();
        if (accForm.password) payload.password = accForm.password;
      }
      if (accForm.account_id) {
        await api.put(`/domains/tpp/accounts/${accForm.account_id}`, payload);
        toast.success("Account updated");
      } else {
        payload.auth_type = accForm.auth_type;
        if (accForm.auth_type === "rest") {
          payload.api_key = accForm.api_key.trim();
          payload.secret_key = accForm.secret_key.trim();
          if (!payload.api_key || !payload.secret_key) {
            toast.error("API Key and Secret Key are required"); setAccBusy(false); return;
          }
        } else if (!accForm.account_no || !accForm.login || !accForm.password) {
          toast.error("Account No, Login and Password are required"); setAccBusy(false); return;
        }
        await api.post("/domains/tpp/accounts", payload);
        toast.success("Account added (credentials encrypted)");
      }
      setShowAccForm(false);
      loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setAccBusy(false); }
  };

  const testAccount = async (a) => {
    try {
      const { data } = await api.post(`/domains/tpp/accounts/${a.account_id}/test`);
      toast.success(data?.message || "Connected");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    }
  };

  const syncAccount = async (a) => {
    setSyncingId(a.account_id);
    try {
      const { data } = await api.post(`/domains/tpp/accounts/${a.account_id}/sync`);
      toast.success(`${a.label}: synced ${data.total} domains (${data.created} new, ${data.updated} updated)`);
      loadAccounts(); loadDomains();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncingId(null); }
  };

  const syncAll = async () => {
    setSyncingId("__all__");
    try {
      const { data } = await api.post("/domains/tpp/sync");
      toast.success(`Synced ${data.total} domains across ${data.per_account?.length || 0} account(s)`);
      loadAccounts(); loadDomains();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncingId(null); }
  };

  const setDefaultAccount = async (a) => {
    try {
      await api.post(`/domains/tpp/accounts/${a.account_id}/default`);
      toast.success(`${a.label} is now the default TPP account`);
      loadAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set default");
    }
  };

  const deleteAccount = async (a) => {
    if (!window.confirm(`Delete "${a.label}"?\n\nAll ${a.last_sync_result?.total || 0} domains synced from this account will also be removed from this app (the domains themselves stay at the registrar).`))
      return;
    try {
      const { data } = await api.delete(`/domains/tpp/accounts/${a.account_id}`);
      toast.success(`Removed account and ${data.domains_removed} domain rows`);
      loadAccounts(); loadDomains();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Queue an assignment change. Called by the row dropdown on every
  // selection; DOES NOT hit the API. The pending map is flushed by
  // ``flushPending`` when the admin clicks UPDATE at the top-right.
  const queueAssign = (domain, clientId) => {
    setPending((prev) => {
      const next = { ...prev };
      // If the pick matches the domain's current client, clear the entry
      // (it's a no-op) — otherwise stash it.
      const current = domain.client_id || "_none";
      if (clientId === current) {
        delete next[domain.domain_id];
      } else {
        next[domain.domain_id] = clientId;
      }
      return next;
    });
  };

  const discardPending = () => setPending({});

  const flushPending = async () => {
    const entries = Object.entries(pending);
    if (!entries.length) return;
    setSaving(true);
    let ok = 0, failed = 0;
    for (const [domain_id, cid] of entries) {
      try {
        await api.patch(`/domains/${domain_id}/assign`, {
          client_id: cid === "_none" ? null : cid,
        });
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setSaving(false);
    setPending({});
    if (failed === 0) {
      toast.success(`Updated ${ok} domain assignment${ok === 1 ? "" : "s"}`);
    } else {
      toast.error(`${ok} updated · ${failed} failed`);
    }
    loadDomains();
  };

  const sortedClients = useMemo(
    () => [...(clients || [])].sort((a, b) =>
      (a.company_name || "").localeCompare(b.company_name || "",
        undefined, { sensitivity: "base" })),
    [clients],
  );

  const pendingCount = Object.keys(pending).length;

  const unassignedCount = useMemo(
    () => domains.filter((d) => !d.client_id).length,
    [domains],
  );

  return (
    <div className="space-y-5" data-testid="domains-page">
      <div>
        <div className="label-overline">Registrars</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight">Domain Names</h1>
        <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
          Connect one or more TPP Wholesale reseller accounts, pull the master
          domain list from each, assign domains to clients and manage nameservers,
          DNS zone records and renewals.
        </p>
      </div>

      {/* ── TPP account list ── */}
      <Card className="rounded-sm" data-testid="tpp-accounts-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-sm grid place-items-center bg-[#0B6FA4] text-white font-display font-bold text-[10px]">
                TPP
              </span>
              <div>
                <h2 className="font-display font-bold text-base tracking-tight">TPP Wholesale accounts</h2>
                <div className="text-[10px] text-zinc-500 font-mono">
                  {accounts.length} configured · auto-syncs daily at 5:30am
                  {serverIp && <> · outbound IP <span className="text-zinc-700">{serverIp}</span></>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" className="rounded-sm h-8"
                onClick={syncAll}
                disabled={!accounts.length || syncingId === "__all__"}
                data-testid="tpp-sync-all">
                {syncingId === "__all__"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                Sync all
              </Button>
              <Button size="sm" onClick={beginAddAccount}
                className="rounded-sm h-8 bg-[#002FA7] hover:bg-[#001f70] text-white"
                data-testid="tpp-add-account">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add TPP account
              </Button>
            </div>
          </div>

          {accountsLoading ? (
            <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading accounts…
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-[11px] text-zinc-500 italic">
              No TPP accounts yet — add one to start pulling domains.
            </div>
          ) : (
            <div className="space-y-1.5">
              {accounts.map((a) => (
                <AccountRow key={a.account_id} account={a}
                  onEdit={() => beginEditAccount(a)}
                  onTest={() => testAccount(a)}
                  onSync={() => syncAccount(a)}
                  onSetDefault={() => setDefaultAccount(a)}
                  onDelete={() => deleteAccount(a)}
                  syncing={syncingId === a.account_id} />
              ))}
            </div>
          )}

          {showAccForm && (
            <AccountForm form={accForm} setForm={setAccForm} busy={accBusy}
              onCancel={() => setShowAccForm(false)} onSave={saveAccount} />
          )}
        </CardContent>
      </Card>

      {/* ── Domain list filters ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search domains…" className="rounded-sm h-9 pl-8 text-sm"
            data-testid="domains-search" />
        </div>
        <select value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)}
          className="h-9 rounded-sm border border-zinc-200 text-xs font-mono px-2 bg-white"
          data-testid="domains-filter-assigned">
          <option value="all">All domains</option>
          <option value="unassigned">Unassigned only</option>
          <option value="assigned">Assigned only</option>
        </select>
        {accounts.length > 1 && (
          <select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}
            className="h-9 rounded-sm border border-zinc-200 text-xs font-mono px-2 bg-white"
            data-testid="domains-filter-account">
            <option value="all">All TPP accounts</option>
            {accounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>{a.label}</option>
            ))}
          </select>
        )}
        <label className="inline-flex items-center gap-1.5 text-xs font-mono text-zinc-600 h-9 px-2 rounded-sm border border-zinc-200 bg-white cursor-pointer"
          data-testid="domains-show-expired-label">
          <input type="checkbox" checked={showExpired}
            onChange={(e) => setShowExpired(e.target.checked)}
            className="accent-[#002FA7]"
            data-testid="domains-show-expired" />
          Show expired
        </label>
        <label className="inline-flex items-center gap-1.5 text-xs font-mono text-zinc-600 h-9 px-2 rounded-sm border border-zinc-200 bg-white cursor-pointer"
          data-testid="domains-show-unassigned-label">
          <input type="checkbox"
            checked={assignedFilter === "unassigned"}
            onChange={(e) => setAssignedFilter(e.target.checked ? "unassigned" : "all")}
            className="accent-[#002FA7]"
            data-testid="domains-show-unassigned" />
          Show unassigned
        </label>
        <span className="text-[11px] font-mono text-zinc-500" data-testid="domains-count">
          {domains.length} shown{unassignedCount > 0 && ` · ${unassignedCount} unassigned`}
        </span>
        <div className="flex-1" />
        {pendingCount > 0 && (
          <>
            <span className="text-[11px] font-mono text-amber-700"
              data-testid="domains-pending-count">
              {pendingCount} pending change{pendingCount === 1 ? "" : "s"}
            </span>
            <Button size="sm" variant="outline" onClick={discardPending}
              disabled={saving} className="rounded-sm h-9 text-xs"
              data-testid="domains-discard-pending">
              Discard
            </Button>
          </>
        )}
        <Button size="sm" onClick={flushPending}
          disabled={saving || pendingCount === 0}
          className="rounded-sm h-9 text-xs font-mono uppercase tracking-wider bg-[#002FA7] hover:bg-[#002080] text-white disabled:opacity-40"
          data-testid="domains-update-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  : <Save className="h-3.5 w-3.5 mr-1" />}
          Update
        </Button>
      </div>

      {loading ? (
        <Card className="rounded-sm"><CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading domains…
        </CardContent></Card>
      ) : domains.length === 0 ? (
        <Card className="rounded-sm border-dashed"><CardContent className="p-10 text-center text-sm text-zinc-500"
          data-testid="domains-empty">
          <Globe2 className="h-6 w-6 mx-auto mb-2 text-zinc-300" />
          {accounts.length === 0
            ? <>No domains yet — add a TPP account above to start pulling the master list.</>
            : showExpired
              ? <>No domains match your filters.</>
              : <>No live domains match — try enabling <strong>Show expired</strong>.</>}
        </CardContent></Card>
      ) : (
        <DomainTable domains={domains} clients={sortedClients} onAssign={queueAssign}
          pending={pending}
          onReload={loadDomains} expanded={expanded} setExpanded={setExpanded}
          multiAccount={accounts.length > 1} />
      )}
    </div>
  );
}

function AccountRow({ account, onEdit, onTest, onSync, onSetDefault, onDelete, syncing }) {
  const a = account;
  const isRest = a.auth_type === "rest";
  return (
    <div className={`border rounded-sm p-2.5 flex items-center gap-2 flex-wrap
      hover:bg-zinc-50/60 transition-colors ${
        a.is_default ? "border-[#002FA7]/40 bg-[#002FA7]/[0.04]" : "border-zinc-200"}`}
      data-testid={`tpp-account-row-${a.account_id}`}>
      <div className="flex items-center gap-2 min-w-[220px] flex-1">
        <Badge variant="outline" className={`rounded-sm text-[9px] font-mono uppercase ${
          isRest ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                 : "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
          {isRest ? "REST" : "Legacy"}
        </Badge>
        {a.is_default && (
          <Badge className="rounded-sm text-[9px] font-mono uppercase bg-[#002FA7] hover:bg-[#002FA7] text-white gap-1"
            data-testid={`tpp-account-default-badge-${a.account_id}`}>
            <Star className="h-2.5 w-2.5 fill-current" /> Default
          </Badge>
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{a.label}</div>
          <div className="text-[10px] font-mono text-zinc-500 truncate">
            {isRest
              ? <>API Key ••••{a.api_key_last4 || "—"}</>
              : <>Acct {a.account_no || "—"} · User {a.login || "—"}</>}
            {a.last_sync_at && <> · last sync {new Date(a.last_sync_at).toLocaleString("en-AU", {dateStyle: "short", timeStyle: "short"})}</>}
            {a.last_sync_result?.total != null && <> · {a.last_sync_result.total} domains</>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {!a.is_default && (
          <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]"
            onClick={onSetDefault} title="Use this account as the default TPP account"
            data-testid={`tpp-account-set-default-${a.account_id}`}>
            <Star className="h-3 w-3 mr-1" /> Make default
          </Button>
        )}
        <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={onTest}
          data-testid={`tpp-account-test-${a.account_id}`}>
          <KeyRound className="h-3 w-3 mr-1" /> Test
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={onSync}
          disabled={syncing}
          data-testid={`tpp-account-sync-${a.account_id}`}>
          {syncing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Sync
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={onEdit}
          data-testid={`tpp-account-edit-${a.account_id}`}>
          <Pencil className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] text-red-600" onClick={onDelete}
          data-testid={`tpp-account-delete-${a.account_id}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function AccountForm({ form, setForm, busy, onCancel, onSave }) {
  const isEdit = !!form.account_id;
  return (
    <div className="border border-[#002FA7]/20 rounded-sm p-3 bg-[#002FA7]/[0.03] space-y-2"
      data-testid="tpp-account-form">
      <div className="flex items-center justify-between">
        <div className="label-overline">{isEdit ? "Edit TPP account" : "Add a new TPP account"}</div>
        <button type="button" onClick={onCancel} className="text-zinc-400 hover:text-zinc-700"
          data-testid="tpp-account-form-cancel">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <Label className="label-overline text-[9px]">Friendly label</Label>
          <Input value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="e.g. Iceberg Main"
            className="mt-0.5 rounded-sm h-9 text-sm bg-white"
            data-testid="tpp-account-label" />
        </div>
        {!isEdit && (
          <div>
            <Label className="label-overline text-[9px]">Auth type</Label>
            <select value={form.auth_type}
              onChange={(e) => setForm({ ...form, auth_type: e.target.value })}
              className="mt-0.5 h-9 w-full rounded-sm border border-zinc-200 text-sm font-mono px-2 bg-white"
              data-testid="tpp-account-auth-type">
              <option value="rest">REST API (recommended)</option>
              <option value="legacy">Legacy HTTP API</option>
            </select>
          </div>
        )}
      </div>

      {form.auth_type === "rest" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label className="label-overline text-[9px]">API Key</Label>
            <Input value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              placeholder="Paste API Key"
              className="mt-0.5 rounded-sm h-9 font-mono text-sm bg-white"
              data-testid="tpp-account-api-key" />
          </div>
          <div>
            <Label className="label-overline text-[9px]">
              Secret Key {isEdit && <span className="text-emerald-600">(leave blank to keep)</span>}
            </Label>
            <Input type="password" value={form.secret_key}
              onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
              placeholder={isEdit ? "••••••••••••" : "Paste Secret Key (shown only once)"}
              className="mt-0.5 rounded-sm h-9 font-mono text-sm bg-white"
              data-testid="tpp-account-secret-key" />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <Label className="label-overline text-[9px]">Account No</Label>
            <Input value={form.account_no}
              onChange={(e) => setForm({ ...form, account_no: e.target.value })}
              placeholder="e.g. 10212"
              className="mt-0.5 rounded-sm h-9 font-mono text-sm bg-white"
              data-testid="tpp-account-no" />
          </div>
          <div>
            <Label className="label-overline text-[9px]">API Login</Label>
            <Input value={form.login}
              onChange={(e) => setForm({ ...form, login: e.target.value })}
              placeholder="e.g. IMAGET-API"
              className="mt-0.5 rounded-sm h-9 font-mono text-sm bg-white"
              data-testid="tpp-account-login" />
          </div>
          <div>
            <Label className="label-overline text-[9px]">
              Password {isEdit && <span className="text-emerald-600">(leave blank to keep)</span>}
            </Label>
            <Input type="password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={isEdit ? "••••••••••••" : "Paste API password"}
              className="mt-0.5 rounded-sm h-9 font-mono text-sm bg-white"
              data-testid="tpp-account-password" />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        <div className="text-[10px] text-zinc-500">
          {form.auth_type === "rest"
            ? "REST unlocks live pricing, autorenew toggles, WHOIS, and full DNS zone CRUD. Secrets are Fernet-encrypted at rest."
            : "Legacy HTTP API — kept as a fallback for existing accounts."}
        </div>
        <Button size="sm" onClick={onSave} disabled={busy}
          className="rounded-sm h-8 bg-zinc-900 hover:bg-black text-white"
          data-testid="tpp-account-save">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
          {isEdit ? "Save changes" : "Add account"}
        </Button>
      </div>
    </div>
  );
}

function DomainTable({ domains, clients, onAssign, pending, onReload, expanded, setExpanded, multiAccount }) {
  return (
    <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
      <div className={`grid gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200
        text-[9px] font-mono uppercase tracking-wider text-zinc-500 ${
          multiAccount ? "grid-cols-14" : "grid-cols-12"}`}
        style={multiAccount ? { gridTemplateColumns: "3fr 1.5fr 1fr 1.5fr 3fr" } : undefined}>
        <div>Domain</div>
        <div>Expiry</div>
        <div>Renewal /yr</div>
        {multiAccount && <div>Account</div>}
        <div>Assigned client</div>
      </div>
      {domains.map((d) => (
        <DomainRow key={d.domain_id} domain={d} clients={clients}
          pendingValue={pending[d.domain_id]}
          expanded={expanded === d.domain_id}
          onToggle={() => setExpanded(expanded === d.domain_id ? null : d.domain_id)}
          onAssign={(cid) => onAssign(d, cid)}
          onReload={onReload}
          multiAccount={multiAccount} />
      ))}
    </div>
  );
}

function DomainRow({ domain: d, clients, pendingValue, expanded, onToggle, onAssign, onReload, multiAccount }) {
  const badge = expiryBadge(d.expiry_date);
  // If the row has a pending edit, show the pending choice — otherwise
  // fall back to the persisted client_id from the API.
  const shownValue = pendingValue !== undefined
    ? pendingValue
    : (d.client_id || "_none");
  const isPending = pendingValue !== undefined;
  return (
    <div className={d.missing_from_registrar ? "opacity-50" : ""}>
      <div className="grid gap-2 px-3 py-2 border-b border-zinc-100 items-center
        hover:bg-zinc-50/60 transition-colors"
        style={{ gridTemplateColumns: multiAccount ? "3fr 1.5fr 1fr 1.5fr 3fr" : "3fr 1.5fr 1fr 3fr" }}
        data-testid={`domain-row-${d.domain_id}`}>
        <button type="button" onClick={onToggle}
          className="flex items-center gap-1.5 text-left min-w-0"
          data-testid={`domain-expand-${d.domain_id}`}>
          {expanded ? <ChevronDown className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
            : <ChevronRight className="h-3.5 w-3.5 text-zinc-400 shrink-0" />}
          <span className="font-mono text-xs truncate">{d.domain}</span>
          {d.autorenew && (
            <Badge variant="outline" className="rounded-sm text-[8px] bg-emerald-50 text-emerald-700 border-emerald-200"
              title="Auto-renew is ON at TPP">↻</Badge>
          )}
          {d.missing_from_registrar && (
            <Badge variant="outline" className="rounded-sm text-[8px] bg-zinc-100">gone?</Badge>
          )}
        </button>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-mono text-[10px] text-zinc-600">{d.expiry_date || "—"}</span>
          <Badge variant="outline" className={`rounded-sm text-[8px] ${badge.cls}`}>{badge.label}</Badge>
        </div>
        <div className="font-mono text-[11px] text-zinc-700"
          data-testid={`domain-renewal-cost-${d.domain_id}`}>
          {d.renewal_cost_api != null
            ? <>${Number(d.renewal_cost_api).toFixed(2)}</>
            : <span className="text-zinc-300">—</span>}
        </div>
        {multiAccount && (
          <div className="text-[10px] font-mono text-zinc-500 truncate"
            title={d.account_label || ""}>
            {d.account_label || "—"}
          </div>
        )}
        <div>
          <select value={shownValue}
            onChange={(e) => onAssign(e.target.value)}
            className={`h-7 w-full rounded-sm border text-[11px] px-1 ${
              isPending
                ? "border-amber-400 bg-amber-50 text-amber-900 ring-1 ring-amber-300"
                : shownValue !== "_none"
                  ? "border-[#002FA7]/30 bg-[#002FA7]/[0.04] text-[#002FA7]"
                  : "border-zinc-200 bg-white text-zinc-500"}`}
            title={isPending ? "Pending — click UPDATE to save" : undefined}
            data-testid={`domain-assign-${d.domain_id}`}>
            <option value="_none">— Unassigned —</option>
            {clients.map((c) => (
              <option key={c.client_id} value={c.client_id}>{c.company_name}</option>
            ))}
          </select>
        </div>
      </div>
      {expanded && <DomainDetailPanel domain={d} onDomainUpdated={onReload} />}
    </div>
  );
}
